import React, { memo, useCallback } from "react";
import { SuggestiveTyping } from "@swiggy-private/dls-web-recipe-food-web/dist/components/SuggestiveTyping";
import { isEmpty } from "@swiggy-private/js-utils";
import type { IFieldOption } from "../Interface/fieldRendererInterface";
import { EXPENSE_CODE_HALF_CARD_TEXTS, MULTI_SUGGESTIVE_DROPDOWN_HEIGHT } from "../constants";
import { EmptyResultsText } from "../styles";
import { getEmptyResultsTextTestId, getMultiSuggestiveFieldTestId } from "@payment-ui/helpers/SuggestiveFieldTestId";
import { isValidEmailId } from "@payment-ui/helpers/String";

type FieldOption = IFieldOption;

interface MultiSuggestiveTypingFieldProps {
    id: string;
    label: string;
    enabled: boolean;
    value: string;
    chips: string[];
    sortedOptions: Array<{
        text: string | React.ReactNode;
        id: number;
        data: {
            suggestion: string;
            value: string;
            uniqueId: string;
        };
        secondaryText?: string | React.ReactNode;
    }>;
    isLoading: boolean;
    isFieldTouched: boolean;
    inputRef: React.RefObject<HTMLInputElement>;
    allOptions: FieldOption[];
    apiCall?: string;
    onChangeValue: (fieldId: string, value: string, allOptions: FieldOption[], apiCall?: string) => void;
    onClick: (fieldId: string, data?: Record<string, unknown>) => void;
    onChipRemove: (fieldId: string, index: number) => void;
    onChipsChange: (fieldId: string, newChips: string[]) => void;
    isDetailsSubmitted?: boolean;
}

const MultiSuggestiveTypingFieldComponent: React.FC<MultiSuggestiveTypingFieldProps> = ({
    id,
    label,
    value,
    chips,
    sortedOptions,
    isLoading,
    isFieldTouched,
    inputRef,
    allOptions,
    apiCall,
    onChangeValue,
    onClick,
    onChipRemove,
    onChipsChange,
    isDetailsSubmitted,
}) => {
    const isSortedOptionsEmpty = sortedOptions.length === 0;

    const shouldShowError =
        isFieldTouched &&
        isSortedOptionsEmpty &&
        value.length > 0 &&
        value.includes("@") &&
        !isLoading &&
        !isValidEmailId(value, 254);

    const handleChipRemove = useCallback((index: number) => onChipRemove(id, index), [id, onChipRemove]);

    const handleChipsChange = useCallback((newChips: string[]) => onChipsChange(id, newChips), [id, onChipsChange]);

    const handleChangeValue = useCallback(
        (newValue: string) => onChangeValue(id, newValue, allOptions, apiCall),
        [id, onChangeValue, allOptions, apiCall],
    );

    const handleClick = useCallback((data?: Record<string, unknown>) => onClick(id, data), [id, onClick]);

    return (
        <SuggestiveTyping
            key={id}
            value={value}
            name={id}
            options={sortedOptions}
            label={label}
            enableMultiValue={true}
            chips={chips}
            onChipsChange={handleChipsChange}
            onChipRemove={handleChipRemove}
            onChangeValue={handleChangeValue}
            maxVisibleChips={3}
            onClick={handleClick}
            inputRef={inputRef}
            chipDelimiter={"\0"}
            containerHeight={!isEmpty(sortedOptions) ? MULTI_SUGGESTIVE_DROPDOWN_HEIGHT : undefined}
            attrs={{
                disabled: isDetailsSubmitted,
                autoComplete: "off",
                "data-testid": getMultiSuggestiveFieldTestId(id),
            }}
            maxChipsHeight={200}
            isDisabled={isDetailsSubmitted}
            disableAutoAddChipOnBlur={true}
            isLoading={isLoading}
            emptyResultsContent={
                shouldShowError ? (
                    <EmptyResultsText
                        variant="V2.body1.neutral.medium"
                        color="text_High_Emphasis"
                        data-testid={getEmptyResultsTextTestId(id)}
                    >
                        {EXPENSE_CODE_HALF_CARD_TEXTS.INVALID_EMAIL}
                    </EmptyResultsText>
                ) : null
            }
        />
    );
};

export default memo(MultiSuggestiveTypingFieldComponent);
